
import Vue from 'vue'
import { Prop, Component } from 'vue-property-decorator'
import axios from 'axios'
import { prettyDate } from '../../../global/functions/util'
import { Job, JobMessage } from '../../../global/models'
import EmptyState from './EmptyState.vue'
import Message from './Message.vue'

@Component({
  components: { EmptyState, Message }
})
export default class JobMessages extends Vue {
  @Prop({ type: Object, required: true })
  private readonly job!: Job

  private messages: JobMessage[] = []

  private messageText: string | null = null

  private sendingMessage = false

  private get isNotOwnJob() {
    if (!this.job) {
      return false
    }

    return this.job.agencyId !== this.user.agencyId && this.job.brokerId !== this.user.agencyId
  }

  private get emptyStateMessage() {
    if (this.isNotOwnJob) {
      return 'You can only message on your own jobs'
    }

    if (this.job.status === 'draft' || this.job.status === 'inactive') {
      return 'Messages cannot be sent while in draft mode'
    }

    return 'Type your first message!'
  }

  private get apiRoute() {
    return `jobs/${this.job.id}/messages`
  }

  private get canAddMessages() {
    if (this.isNotOwnJob) {
      return false
    }

    return this.job.status !== 'draft' && this.job.status !== 'inactive'
  }

  private get user() {
    return this.$store.state.auth
  }

  private get messagesContainer() {
    return this.$refs.messagesContainer as HTMLDivElement
  }

  beforeMount() {
    this.joinRoom()
    this.$socket().io.on('reconnect', this.joinRoom)
    this.$socket().on('jobs:new-message', this.onNewMessage)
  }

  async mounted() {
    const response = await axios.get<JobMessage[]>(this.apiRoute)
    this.messages = response.data
    this.scrollToBottom()
  }

  beforeDestroy() {
    this.$socket().emit('jobs:leave-room', { jobId: this.job.id })
    this.$socket().io.off('reconnect', this.joinRoom)
    this.$socket().off('jobs:new-message', this.onNewMessage)
  }

  onNewMessage(message: JobMessage) {
    if (message.jobId === this.job.id) {
      this.messages.push(message)
      this.scrollToBottom()

      if (message.authorType === 'system') {
        this.$emit('refresh')
      }

      if (!document.hidden) {
        this.$socket().emit('jobs:read-messages', { jobId: this.job.id })
      } else {
        this.$emit('unseen-message')
      }
    }
  }

  joinRoom() {
    this.$socket().emit('jobs:join-room', { jobId: this.job.id })
  }

  scrollToBottom() {
    this.$nextTick(() => {
      if (this.messagesContainer) {
        this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight
      }
    })
  }

  messageIndex(message: JobMessage) {
    let index = -1
    if (message.id) {
      index = this.messages.findIndex(i => i.id === message.id)
    } else {
      index = this.messages.findIndex(i => i.text === message.text && i.authorId === message.authorId && i.created === message.created)
    }
    return index
  }

  aboveMessage(message: JobMessage) {
    const index = this.messageIndex(message)

    if (index - 1 < 0) {
      return null
    }
    return this.messages[index - 1]
  }

  sameAuthorBelow(message: JobMessage) {
    const index = this.messages.findIndex(i => i.id === message.id)

    if (index + 1 === this.messages.length) {
      return false
    }

    return this.sameAuthor(message, this.messages[index + 1])
  }

  ownMessage(message: JobMessage) {
    return this.user.agencyId === message.authorId
  }

  sameAuthorAbove(message: JobMessage) {
    const aboveMessage = this.aboveMessage(message)
    if (!aboveMessage) {
      return false
    }

    return this.sameAuthor(message, aboveMessage)
  }

  sameAuthor(a: JobMessage, b: JobMessage) {
    return a.authorType === b.authorType && a.authorId === b.authorId
  }

  sameDateAbove(message: JobMessage) {
    const aboveMessage = this.aboveMessage(message)
    if (!aboveMessage) {
      return false
    }

    return prettyDate(message.created) === prettyDate(aboveMessage.created)
  }

  submitOnEnter(event: KeyboardEvent) {
    if (event.keyCode === 13 && !event.shiftKey) {
      this.sendMessage()
    }
  }

  makeOfflineMessageVersion({ text, jobId }: { text: string | null, jobId: string }) {
    let author = ''
    if (this.user.agencyId === this.job.agencyId) {
      author = this.job.agencyName || ''
    } else {
      author = this.job.brokerName || ''
    }
    return {
      text: text || '',
      jobId,
      authorType: 'agency' as const,
      authorId: this.user.agencyId,
      author,
      created: new Date(),
      updated: new Date()
    }
  }

  sendMessage() {
    if (!this.messageText || !this.messageText.trim()) {
      return
    }

    try {
      const data = { text: this.messageText.trim(), jobId: this.job.id }
      this.$socket().emit('jobs:send-message', data)
      this.messageText = null
      this.messages.push(this.makeOfflineMessageVersion(data))
      this.scrollToBottom()
      this.$nextTick(() => {
        (this.$refs.messageBox as any).$refs.input.focus()
      })
    } catch (error) {
      this.$store.dispatch('error', 'Error creating message')
    }
  }
}
